"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { Check, Undo2 } from "lucide-react";
import { MilkdownProvider } from "@milkdown/react";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Separator } from "@/components/shadcn/separator";
import { InputSpec } from "@/generated";
import { useHeuristicEvaluation } from "@/hooks/useHeuristicEvaluation";
import {
  appendNodeConfigContext,
  useNodeConfigContext,
} from "@/hooks/useNodeConfigContext";
import { HeuristicScoreBadge } from "@/components/Form/TiptapEditor/HeuristicScoreBadge";
import {
  ComplexMarkdownEditor,
  SlashVariableHint,
} from "./ComplexMarkdownField";
import { AIComplexMarkdownDialog } from "./AIComplexMarkdownDialog";
import type { EditorInput } from "./types";

interface AIComplexMarkdownFieldProps {
  control: Control<Record<string, unknown>>;
  name: string;
  label?: string;
  // Title shown in the expand dialog when no inline `label` is rendered (e.g.
  // when a parent field already renders its own label).
  dialogTitle?: string;
  tooltip?: string;
  placeholder?: string;
  isDisabled?: boolean;
  inputs: EditorInput[];
  invalid?: boolean;
  onBlur?: () => void;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  improverPrompt?: string;
  enableHeuristics?: boolean;
  heuristicContext?: string;
  heuristicTypes?: string[];
  improverContext?: string;
  includeNodeConfigContext?: boolean;
}

function toVersions(value: unknown): string[] {
  const toText = (v: unknown): string => {
    if (typeof v === "string") return v;
    if (
      v &&
      typeof v === "object" &&
      "text" in v &&
      typeof (v as { text: unknown }).text === "string"
    ) {
      return (v as { text: string }).text;
    }
    return "";
  };
  if (Array.isArray(value)) return value.map(toText);
  const text = toText(value);
  return text ? [text] : [];
}

export function AIComplexMarkdownField(props: AIComplexMarkdownFieldProps) {
  const nodeConfigContext = useNodeConfigContext(props.control, props.name);
  const contextToAppend =
    props.includeNodeConfigContext === false ? null : nodeConfigContext;
  const heuristicContext = useMemo(
    () => appendNodeConfigContext(props.heuristicContext, contextToAppend),
    [contextToAppend, props.heuristicContext]
  );
  const improverContext = useMemo(
    () => appendNodeConfigContext(props.improverContext, contextToAppend),
    [contextToAppend, props.improverContext]
  );

  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field: { value, onChange, onBlur: fieldOnBlur } }) => (
        <AIComplexMarkdownInner
          {...props}
          heuristicContext={heuristicContext}
          improverContext={improverContext}
          versions={toVersions(value)}
          onChangeVersions={onChange}
          onBlur={() => {
            fieldOnBlur();
            props.onBlur?.();
          }}
        />
      )}
    />
  );
}

interface InnerProps extends AIComplexMarkdownFieldProps {
  versions: string[];
  onChangeVersions: (next: string[]) => void;
}

function AIComplexMarkdownInner({
  versions,
  onChangeVersions,
  label,
  dialogTitle,
  tooltip,
  placeholder,
  isDisabled,
  inputs,
  invalid,
  onBlur,
  nodeId,
  variableBindings,
  name,
  enableHeuristics,
  heuristicContext,
  heuristicTypes,
  improverContext,
}: InnerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dialogInitialHeuristicsOpen, setDialogInitialHeuristicsOpen] =
    useState(false);
  const hasAutoRetriedOnModalOpenRef = useRef(false);

  const currentText = versions.length ? versions[versions.length - 1] : "";

  // While the dialog is open it drives the heuristic text (its local edits are
  // not committed to the form until close); otherwise the field value wins.
  const [heuristicTextOverride, setHeuristicTextOverride] = useState<
    string | null
  >(null);
  const heuristicText = heuristicTextOverride ?? currentText;

  const handleHeuristicTextChange = useCallback(
    (text: string | null) => setHeuristicTextOverride(text),
    []
  );

  const {
    results: heuristicResults,
    isLoading: isLoadingHeuristics,
    isError: isHeuristicsError,
    errorMessage: heuristicsErrorMessage,
    isOutdated: isHeuristicsOutdated,
    canAnalyse: canAnalyseHeuristics,
    triggerEvaluation: triggerHeuristicEvaluation,
    scheduleReanalysis: scheduleHeuristicReanalysis,
    revertAnalysis: revertHeuristicAnalysis,
  } = useHeuristicEvaluation({
    text: heuristicText,
    enabled: enableHeuristics ?? false,
    context: heuristicContext,
    heuristicTypes,
  });

  const improverInputs: InputSpec[] = useMemo(
    () =>
      inputs.map((input) => ({
        name: input.id,
        kind: "value" as const,
        label:
          input.sourceName && input.sourceName !== input.name
            ? `${input.sourceName} / ${input.name}`
            : input.name,
      })),
    [inputs]
  );

  // Build the improver context here (rather than letting HeuristicsSection do
  // it from chip options) so variables are enforced in the milkdown `:var[id]`
  // directive syntax instead of the TipTap `((id))` syntax.
  const fullImproverContext = useMemo(() => {
    const base = improverContext ?? "";
    if (!improverInputs.length) return base || undefined;
    const varList = improverInputs.map((i) => `:var[${i.name}]`).join(", ");
    return (
      `${base}\n` +
      `Available input variables, written exactly in this directive form: ` +
      `${varList}\n` +
      `Keep every input variable the prompt already uses. Add another one only ` +
      `when the task needs it.\n` +
      `Do not rename them or change the :var[...] syntax.`
    ).trim();
  }, [improverContext, improverInputs]);

  const updateLastVersion = useCallback(
    (md: string) => {
      const next = versions.length
        ? versions.map((v, i) => (i === versions.length - 1 ? md : v))
        : [md];
      // Collapse a lone empty version to `[]` so a cleared prompt still reads
      // as empty for the backend's `required` check (parity with the old
      // plain-string field).
      onChangeVersions(next.length === 1 && next[0] === "" ? [] : next);
    },
    [onChangeVersions, versions]
  );

  const handleAccept = useCallback(() => {
    if (versions.length > 1) onChangeVersions(versions.slice(1));
  }, [onChangeVersions, versions]);

  const handleRevert = useCallback(() => {
    if (versions.length > 1) {
      onChangeVersions(versions.slice(0, -1));
      revertHeuristicAnalysis?.();
    }
  }, [onChangeVersions, versions, revertHeuristicAnalysis]);

  const hasPendingRefinement = versions.length > 1;

  const heuristicScore =
    heuristicResults?.weighted_score != null
      ? heuristicResults.weighted_score * 10
      : null;

  const toolbarExtra =
    enableHeuristics && !isDisabled ? (
      <div className="flex items-center gap-1">
        <HeuristicScoreBadge
          score={heuristicScore}
          isLoading={isLoadingHeuristics}
          isOutdated={isHeuristicsOutdated}
          isError={isHeuristicsError}
          errorMessage={heuristicsErrorMessage}
          onClick={() => {
            setDialogInitialHeuristicsOpen(true);
            setIsExpanded(true);
            if (isHeuristicsError && !hasAutoRetriedOnModalOpenRef.current) {
              hasAutoRetriedOnModalOpenRef.current = true;
              triggerHeuristicEvaluation(true);
            } else if (
              !heuristicResults &&
              canAnalyseHeuristics &&
              !isHeuristicsError
            ) {
              triggerHeuristicEvaluation();
            }
          }}
        />
        <Separator orientation="vertical" />
      </div>
    ) : undefined;

  return (
    <div className="w-full">
      {label && (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={name}
          isDisabled={isDisabled}
        />
      )}
      <div className="relative">
        <MilkdownProvider>
          <ComplexMarkdownEditor
            value={currentText}
            onChange={updateLastVersion}
            placeholder={placeholder}
            isDisabled={isDisabled}
            inputs={inputs}
            invalid={invalid}
            onBlur={onBlur}
            nodeId={nodeId}
            variableBindings={variableBindings}
            onExpand={
              isDisabled
                ? undefined
                : () => {
                    setDialogInitialHeuristicsOpen(false);
                    setIsExpanded(true);
                  }
            }
            toolbarExtra={toolbarExtra}
          />
        </MilkdownProvider>

        {hasPendingRefinement && (
          <div
            className="pointer-events-none absolute inset-0 z-[5] rounded-md border border-blue-400/60 bg-blue-500/5"
            aria-hidden
          />
        )}
        {hasPendingRefinement && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-stretch rounded-lg border border-border bg-background shadow-md">
            <button
              onClick={handleRevert}
              className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Undo2 size={15} />
              Revert
            </button>
            <Separator orientation="vertical" />
            <button
              onClick={handleAccept}
              disabled={isDisabled}
              className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:text-foreground/80 transition-colors disabled:opacity-50"
              tabIndex={-1}
            >
              <Check size={15} className="text-emerald-600" />
              Accept
            </button>
          </div>
        )}
      </div>

      <SlashVariableHint />

      {isExpanded && (
        <AIComplexMarkdownDialog
          isOpen={isExpanded}
          onOpenChange={setIsExpanded}
          content={versions}
          onSave={onChangeVersions}
          inputs={inputs}
          label={dialogTitle ?? label}
          placeholder={placeholder}
          isDisabled={isDisabled}
          nodeId={nodeId}
          variableBindings={variableBindings}
          enableHeuristics={enableHeuristics}
          initialHeuristicsOpen={dialogInitialHeuristicsOpen}
          heuristicResults={heuristicResults}
          isLoadingHeuristics={isLoadingHeuristics}
          isHeuristicsOutdated={isHeuristicsOutdated}
          isHeuristicsError={isHeuristicsError}
          heuristicsErrorMessage={heuristicsErrorMessage}
          canAnalyseHeuristics={canAnalyseHeuristics}
          triggerHeuristicEvaluation={triggerHeuristicEvaluation}
          scheduleHeuristicReanalysis={scheduleHeuristicReanalysis}
          revertHeuristicAnalysis={revertHeuristicAnalysis}
          onHeuristicTextChange={handleHeuristicTextChange}
          improverContext={fullImproverContext}
          improverInputs={improverInputs}
        />
      )}
    </div>
  );
}
